/* eslint-disable no-case-declarations */
import { updateCommonErrorMessage, updateIsLoadingForRenderBoundary } from "@/redux/actions/lightningAction";
import { RootState } from "@/redux/reducer/store";
import { LightningColorArray, LightningSearchTypeEnum, LightningTabsEnum } from "@/util/cosntants";
import { IFeatureCollectionModel, IGeometryModel } from "@/util/http/Lightning/responseType";
import { useMap } from "@vis.gl/react-google-maps";
import { all } from "axios";
import localforage from "localforage";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";


// 定义一个接口，用于存储每个国家及其对应的多边形数组
interface CountryPolygonMap {
    [countryName: string]: google.maps.Polygon[];
}

interface IRegionBoundaryPolygonOverlayProps {
    searchType?: string | null; // 新增的属性
    currentTab: LightningTabsEnum
}

const strokeColor = LightningColorArray.Green; // 默认边框颜色
const strokeOpacity = 1; // 默认边框透明度
const strokeWeight = 3; // 默认边框宽度

const RegionBoundaryPolygonOverlay = (prop: IRegionBoundaryPolygonOverlayProps) => {
    const { searchType,currentTab } = prop;
    const map = useMap(); // 获取地图实例

    const dispatch = useDispatch();

    const selectedCountry = useSelector((state: RootState) => state.lightningReducer.selectedCountry);
    const [isClear,setIsClear] = useState<boolean>(false);

    useEffect(()=>{
        if(currentTab === LightningTabsEnum.Region){
            setIsClear(false); // 如果当前 tab 是 Region，则不清除
        }
        else{
            setIsClear(true); // 如果不是 Region，则清除
            dispatch(updateIsLoadingForRenderBoundary(false)); // 确保在切换 tab 时不再加载边界
            clearAllBoundaries(); // 清除所有区域
        }
    },[currentTab]);

    // 定义填充颜色，根据 selectType 动态计算
    const getFillColor = useCallback(():[string | undefined,number] => {
        const tmpFillColor = searchType === LightningSearchTypeEnum.ALLCLEAR ? LightningColorArray.AllClear : undefined; // 如果不是 'Risk'，则不设置 fillColor
        const tmpFillOpacity = searchType === LightningSearchTypeEnum.ALLCLEAR ? 0.3 : 0;
        return [tmpFillColor,tmpFillOpacity];
    }, [searchType]);

    // 使用 useRef 来存储当前渲染的每个国家的 Polygon 实例，避免重新渲染导致丢失
    const countryPolygonsRef = useRef<CountryPolygonMap>({});


    // 辅助函数：根据 GeoJSON 几何体获取 Google Maps Polygon 的路径
    const getGooglePolygonPaths = (geometry: IGeometryModel): google.maps.LatLngLiteral[][] => {
        const pathsList: google.maps.LatLngLiteral[][] = [];

        if (!geometry.type || !geometry.coordinates) return pathsList;

        switch (geometry.type) {
            case "Polygon":
                const polygonCoords = (geometry.coordinates as number[][][])[0];
                pathsList.push(polygonCoords.map(([lng, lat]) => ({ lat, lng })));
                break;

            case "MultiPolygon":
                const multiPolygonCoords = (geometry.coordinates as number[][][][]) || [];
                for (const polygon of multiPolygonCoords) {
                    const outerRing = polygon[0];
                    pathsList.push(outerRing.map(([lng, lat]) => ({ lat, lng })));
                }
                break;

            default:
                console.warn(`Unsupported geometry type: ${geometry.type}`);
                break;
        }

        return pathsList;
    };

    // 绘制单个国家的边界
    const drawCountryBoundary = (countryName: string, boundaryData: IFeatureCollectionModel): google.maps.Polygon[] => {
        if (!map) return [];

        const polygons: google.maps.Polygon[] = [];
        if (!boundaryData.features || !Array.isArray(boundaryData.features)) {
            return [];
        }

        // 如果 circleRef.current 已经存在，则更新其属性
        const [fillColor,fillOpacity] = getFillColor();

        boundaryData.features.forEach(feature => {
            if (!feature.geometry) return;

            const pathsList = getGooglePolygonPaths(feature.geometry);
            pathsList.forEach(paths => {
                const polygon = new google.maps.Polygon({
                    paths,
                    strokeColor,
                    strokeOpacity,
                    strokeWeight,
                    fillColor: fillColor,
                    fillOpacity: fillOpacity,
                    map,
                    zIndex: 99
                });
                polygons.push(polygon);
            });
        });
        return polygons;
    };

    // 清除单个国家的边界
    const clearCountryBoundary = (countryName: string) => {
        if (countryPolygonsRef.current[countryName]) {
            countryPolygonsRef.current[countryName].forEach(polygon => polygon.setMap(null));
            delete countryPolygonsRef.current[countryName];
        }
    };

    // 清除所有区域的辅助函数
    const clearAllBoundaries = () => {
        Object.values(countryPolygonsRef.current).forEach(polygons => {
            polygons.forEach(polygon => polygon.setMap(null));
        });
        countryPolygonsRef.current = {}; // 清空引用
        console.log("All boundaries cleared.");
    };

    // --- 新增的 useEffect，用于处理 isClear 属性 ---
    useEffect(() => {
        if (!map) return;

        if (isClear) {
            dispatch(updateIsLoadingForRenderBoundary(true));
            clearAllBoundaries(); // 清除所有区域

            // 设置地图中心点为默认值
            map.setCenter({ lat: 38.0, lng: -95.0 });
            map.setZoom(5);

            dispatch(updateIsLoadingForRenderBoundary(false));
            console.log("Map cleared and reset to default center.");
        }
    }, [isClear, map]);

    console.log("lanxue region selectedCountry",selectedCountry);

    // --- 主要的副作用，处理 selectedCountry 的变化 ---
    useEffect(() => {
        if (!map) return;

        // 如果 isClear 为 true，则不执行 selectedCountry 的处理逻辑
        if (isClear) {
            return;
        }

        dispatch(updateIsLoadingForRenderBoundary(true));
        // 获取当前已渲染的国家列表
        const currentRenderedCountries = Object.keys(countryPolygonsRef.current);

        // 1. 处理需要删除的国家
        const countriesToRemove = currentRenderedCountries.filter(
            country => country !== "USA"&& !selectedCountry?.includes(country)
        );
        countriesToRemove.forEach(country => {
            clearCountryBoundary(country);
            console.log(`Removed boundary for: ${country}`);
        });

        // 2. 处理需要添加的国家
        const countriesToAdd = selectedCountry.filter(
            country => country !== "USA" && !currentRenderedCountries.includes(country)
        );


        const addBoundariesPromises = countriesToAdd.map(async (country) => {
            try {
                const rawData = await localforage.getItem<string>(country);
                const boundaryData = rawData ? JSON.parse(rawData) as IFeatureCollectionModel : null;

                if (boundaryData) {
                    const polygons = drawCountryBoundary(country, boundaryData);
                    if (polygons.length > 0) {
                        countryPolygonsRef.current[country] = polygons;
                    }
                } else {
                    console.warn(`No boundary data found for ${country}`);
                }
            } catch (error) {
                console.error(`Failed to load or draw boundary for ${country}:`, error);
            }
        });

        Promise.all(addBoundariesPromises).then(() => {
            if (countriesToAdd.length > 0) {
                dispatch(updateIsLoadingForRenderBoundary(false));
            }
            // 在所有添加和删除操作完成后，重新计算并设置地图中心点
            updateMapCenter();
        });

        dispatch(updateIsLoadingForRenderBoundary(false));

        // 清理函数
        return () => {
            // 在这里不执行清理，因为增量更新的逻辑会在 isClear 或 selectedCountry 变化时管理
            // 只有当组件整体卸载时才需要完整清理，但通常 isClear 为 true 时已经做了
            // 考虑到 React 18 的 StrictMode 可能导致二次渲染，这里让 isClear 逻辑主导
            // 或者，如果你需要一个在组件卸载时绝对清理的机制，可以保持以下代码：
            // Object.values(countryPolygonsRef.current).forEach(polygons => {
            //     polygons.forEach(polygon => polygon.setMap(null));
            // });
            // countryPolygonsRef.current = {};
        };

    }, [selectedCountry, map, isClear]); // 依赖项：selectedCountry, map, isClear

    // ---- 新的 useEffect，用于处理 searchType 的变化 所带来的颜色填充变化----
    useEffect(()=>{
        if(!map) return;

        const [fillColor,fillOpacity] = getFillColor();

        Object.values(countryPolygonsRef.current).forEach(polygons => {
            polygons.forEach(polygon => {
                polygon.setOptions({
                    fillColor: fillColor,
                    fillOpacity: fillOpacity
                });
            });
        });
    },[map,getFillColor]);

    // --- 新增的辅助函数：计算并更新地图中心点 ---
    const updateMapCenter = () => {
        if (!map) return;

        const allFirstPoints: google.maps.LatLngLiteral[] = [];
        const totalLat = 0;
        const totalLng = 0;
        const count = 0;
        const bounds = new google.maps.LatLngBounds(); // 用于计算多边形的边界

        Object.values(countryPolygonsRef.current).forEach(polygons => {
            polygons?.forEach(polygon => {
                const paths = polygon?.getPaths();
                paths?.forEach(path => {
                    path?.forEach(point => {
                        bounds.extend(point);
                    });
                });
            });
        });

        // if (count > 0) {
        //     const avgLat = totalLat / count;
        //     const avgLng = totalLng / count;
        //     map.setCenter({ lat: avgLat, lng: avgLng });
        //     map.setZoom(7);
        //     console.log(`Calculated new center: {lat: ${avgLat}, lng: ${avgLng}}`);
        if(!bounds?.isEmpty()){
            // 如果有有效的边界数据，设置地图中心点为边界的中心点
            const center = bounds?.getCenter();
            map?.fitBounds(bounds);
            console.log(`Calculated new center: {lat: ${center?.lat()}, lng: ${center?.lng()}}, zoom level: ${map?.getZoom()}`);
            if(map?.getZoom() < 5){
                map?.setZoom(5); // 确保缩放级别不低于 5
            }
            // for all usa
            if(selectedCountry?.length === 1 && selectedCountry[0] === "USA"){
                map?.setZoom(3); // 如果只选中 USA，缩放到 4
            }
        } else {
            // 如果没有选中的国家，或者没有有效的边界数据，将地图中心点设回默认值
            map.setCenter({ lat: 38.0, lng: -95.0 });
            map.setZoom(5); // 也可以重置缩放
            console.log("No countries selected or no valid boundary data, resetting to default center.");
        }
    };


    // 在组件挂载或 map 实例可用时，如果 selectedCountry 不为空，则初始化中心点
    useEffect(() => {
        if (map && selectedCountry?.length > 0 && !isClear) {
            // 初始渲染时，或者 selectedCountry 变为非空时，计算中心点
            if(selectedCountry?.length === 1 && selectedCountry[0] === "USA"){
                map.setCenter({ lat: 38.0, lng: -95.0 });
                map.setZoom(3);
                // return;
            }
            else{
                updateMapCenter();
            }

        } else if (map && selectedCountry?.length === 0 && !isClear) {
            // 如果 selectedCountry 为空且 isClear 为 false，重置到默认中心
            map.setCenter({ lat: 38.0, lng: -95.0 });
            map.setZoom(5);
        }
    }, [map, selectedCountry, isClear]); // 依赖项，确保在它们变化时触发

    return null;
};

export default RegionBoundaryPolygonOverlay;